import React, { useState, useContext, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from 'recharts';
import Main from "../components/layout/Main";
import graphImg from "../assets/images/graphImg.png";
import golfIconDark from "../assets/images/golfIconDark.png";
import handicapRangeIcon from "../assets/images/handicapRangeIcon.png";
import { useNavigate, useLocation } from "react-router-dom";
import useRequest from "../../src/hooks/useRequest";

function Handicap() {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [average, setAverage] = useState();
  const [handicap, setHandicap] = useState();
  const [gameScores, setGameScores] = useState([]);
  const [calculatedStats, setCalculatedStats] = useState({
    lowHandicap: 0,
    differentialAvg: 0,
    antiHandicap: 0,
    totalGames: 0,
    bestScoresCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [show, scoreCardStepModal] = useState(false);
  
  const scoreCardStepModalClose = () => scoreCardStepModal(false);
  const scoreCardStepModalOpen = () => scoreCardStepModal(true);

  useEffect(() => {
    console.log("gameScores state updated:", gameScores);
    console.log("gameScores length:", gameScores ? gameScores.length : 'null/undefined');
  }, [gameScores]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await fetchAverageData();
      await fetchHandicapData();
      await fetchGameScoresData();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAverageData = () => {
    return new Promise((resolve) => {
      request({
        url: `app/game/get_lowest_highest_Score`,
        method: "GET",
        onSuccess: (data) => {
          console.log(data);
          setAverage(data.data);
          resolve();
        },
        onError: (error) => {
          console.log(error);
          resolve();
        },
      });
    });
  };

  const fetchHandicapData = () => {
    return new Promise((resolve) => {
      request({
        url: `/app/game/getHandicapScore`,
        method: "GET",
        onSuccess: (data) => {
          setHandicap(data.data);
          resolve();
        },
        onError: (error) => {
          console.log(error);
          resolve();
        },
      });
    });
  };

  const fetchGameScoresData = () => {
    return new Promise((resolve, reject) => {
      request({
        url: `/app/game/getHandicapGameScores`,
        method: "GET",
        onSuccess: (data) => {
          console.log("Game scores data:", data);
          
          // Handle your exact nested data structure: data.data.data
          let scoresArray = [];
          if (data.data && Array.isArray(data.data.data)) {
            scoresArray = data.data.data;
          } else if (Array.isArray(data.data)) {
            scoresArray = data.data;
          }
          
          console.log("Final scoresArray:", scoresArray);
          
          setGameScores(scoresArray);
          calculateStatsFromGameScores(scoresArray);
          
          resolve(scoresArray);
        },
        onError: (error) => {
          console.error("Error fetching game scores:", error);
          setGameScores([]);
          reject(error);
        },
      });
    });
  };

  const calculateStatsFromGameScores = (games) => {
    if (!games || games.length === 0) {
      setCalculatedStats({
        lowHandicap: 0,
        differentialAvg: 0,
        antiHandicap: 0,
        totalGames: 0,
        bestScoresCount: 0
      });
      return;
    }

    const validDifferentials = games
      .filter(game => game.handicapDifferential !== null && !isNaN(game.handicapDifferential))
      .map(game => game.handicapDifferential);

    const bestScores = games
      .filter(game => game.countsTowardsHandicap && game.handicapDifferential !== null)
      .map(game => game.handicapDifferential);

    const totalGames = games.length;
    const bestScoresCount = bestScores.length;
    
    const lowHandicap = bestScores.length > 0 
      ? bestScores.reduce((sum, diff) => sum + diff, 0) / bestScores.length 
      : 0;

    const differentialAvg = validDifferentials.length > 0 
      ? validDifferentials.reduce((sum, diff) => sum + diff, 0) / validDifferentials.length 
      : 0;

    const antiHandicap = validDifferentials.length > 0 
      ? Math.max(...validDifferentials) 
      : 0;

    setCalculatedStats({
      lowHandicap: parseFloat(lowHandicap.toFixed(2)),
      differentialAvg: parseFloat(differentialAvg.toFixed(2)),
      antiHandicap: parseFloat(antiHandicap.toFixed(2)),
      totalGames,
      bestScoresCount
    });
  };

  const handleScoreClick = (gameId) => {
    console.log("Navigating to game:", gameId);
    navigate(`/allScoreData/${gameId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateForChart = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit'
    });
  };

  // UPDATED: Create chart data with totalScore as the main value
  const chartData = gameScores?.map((game, index) => {
    const differential = game.handicapDifferential;
    const totalScore = game.totalScore || 0; // Main value for the chart
    
    return {
      // CRITICAL: Use unique identifier for each data point
      uniqueKey: `${game._id}_${index}`,
      index: index,
      id: game._id,
      gameNumber: index + 1,
      // FIXED: Use gameNumber for X-axis instead of date to ensure uniqueness
      displayLabel: `Game ${index + 1}`,
      date: formatDateForChart(game.createdAt),
      totalScore: totalScore, // This will be the main bar value
      differential: differential !== null && !isNaN(differential) ? Number(differential) : 0,
      countsTowardsHandicap: game.countsTowardsHandicap || false,
      courseName: game.courseTee?.course?.name || 'Unknown Course',
      teeName: game.courseTee?.name || 'Unknown Tee',
      holes: game.holes || '18',
      fullDate: formatDate(game.createdAt),
      rawDifferential: differential,
      timestamp: game.createdAt,
      // Add all original game data for debugging
      originalGame: game
    };
  }) || [];

  console.log("Chart data created:", chartData.length, "points");

  // UPDATED TOOLTIP - Now shows score as primary info
  const CustomTooltip = ({ active, payload, label }) => {
    console.log("=== TOOLTIP DEBUG START ===");
    console.log("Tooltip active:", active);
    console.log("Tooltip label:", label);
    console.log("Tooltip payload:", payload);

    if (!active || !payload || payload.length === 0) {
      console.log("Tooltip: Not active or no payload");
      return null;
    }

    // Get the actual data point from the payload
    const dataPoint = payload[0]?.payload;
    console.log("Tooltip dataPoint:", dataPoint);

    if (!dataPoint) {
      console.log("Tooltip: No data point found");
      return null;
    }

    console.log("=== TOOLTIP DEBUG END ===");

    return (
      <div className="bg-white p-3 border rounded shadow-lg" style={{ minWidth: '250px', zIndex: 1000 }}>
        <div className="mb-2">
          <small className="text-primary fw-bold">Game #{dataPoint.gameNumber}</small>
        </div>
        <div className="mb-2">
          <div className="fw-medium">{dataPoint.courseName}</div>
          <div className="text-muted small">{dataPoint.teeName} • {dataPoint.holes} holes</div>
        </div>
        <div className="mb-2">
          <div>Total Score: <strong className="text-success fs-5">{dataPoint.totalScore}</strong></div>
          <div>Differential: <strong className="text-dark">
            {dataPoint.differential !== null ? dataPoint.differential.toFixed(1) : 'N/A'}
          </strong></div>
        </div>
        <div className="mb-2">
          <small className="text-muted">{dataPoint.fullDate}</small>
        </div>
        {dataPoint.countsTowardsHandicap && (
          <div className="mb-2">
            <small className="badge bg-success">✓ Best 8 Score</small>
          </div>
        )}
        <div className="border-top pt-2">
          <small className="text-muted">Click to view details</small>
        </div>
      </div>
    );
  };

  // FIXED BAR CLICK HANDLER
  const handleBarClick = (data, index, event) => {
    console.log("=== BAR CLICK DEBUG ===");
    console.log("Click data:", data);
    console.log("Click index:", index);
    console.log("Click event:", event);
    
    if (data && data.id) {
      console.log("Navigating to game ID:", data.id);
      handleScoreClick(data.id);
    } else {
      console.log("No valid game ID found in click data");
    }
  };

  // FIXED: Custom label formatter for X-axis to show dates but use unique keys
  const formatXAxisLabel = (value, index) => {
    const dataPoint = chartData[index];
    return dataPoint ? dataPoint.date : value;
  };

  // NEW: Custom label component for showing scores on top of bars
  const CustomLabel = (props) => {
    const { x, y, width, value } = props;
    return (
      <text 
        x={x + width / 2} 
        y={y - 5} 
        fill="#333" 
        textAnchor="middle" 
        dy={-6}
        fontSize="12"
        fontWeight="600"
      >
        {value}
      </text>
    );
  };

  if (loading) {
    return (
      <Main>
        <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Main>
    );
  }

  return (
    <Main>
      <section className="home-wrap px-lg-4 px-3 pb-lg-4 pb-3 container-fluid">
        <div className="row g-3">
          <div className="col-12">
            <div className="row g-3">
              <div className="col-xl-8">
                <div className="handicapeCard p-3 rounded-3 bg-white h-100 d-flex flex-column flex-nowrap">
                  <div className="handicapeCardHeader">
                    <div className="title mb-1">Game Scores</div>
                    <div className="graphIndicater d-flex flex-column flex-sm-row align-items-sm-center gap-sm-4 gap-2 mb-3">
                      <div className="graphIndicaterItem handicap d-flex gap-1">
                        <span className="badge bg-success me-1"></span>
                        Best 8 Scores (Count Towards Handicap)
                      </div>
                      <div className="graphIndicaterItem d-flex gap-1">
                        <span className="badge bg-secondary me-1"></span>
                        Other Scores
                      </div>
                    </div>
                  </div>
                  
                  <div className="handicapeCardBody flex-fill h-100">
                    {/* Debug info */}
                    <div className="mb-3">
                      <small className="text-muted">
                        Games: {gameScores ? gameScores.length : 'null'}, 
                        Chart points: {chartData.length},
                        Best count: {calculatedStats.bestScoresCount}
                      </small>
                    </div>

                    {gameScores && gameScores.length > 0 ? (
                      <div className="chart-container">
                        {/* Mobile: Horizontal scroll wrapper */}
                        <div className="d-block d-md-none">
                          <div className="position-relative">
                            <div 
                              style={{ 
                                width: `${Math.max(400, chartData.length * 50)}px`, 
                                height: '300px',
                                overflowX: 'auto',
                                overflowY: 'hidden'
                              }}
                              className="border rounded bg-light"
                            >
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={chartData}
                                  margin={{
                                    top: 30,
                                    right: 10,
                                    left: 10,
                                    bottom: 50,
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                                  <XAxis 
                                    dataKey="displayLabel"
                                    tickFormatter={formatXAxisLabel}
                                    angle={-45}
                                    textAnchor="end"
                                    height={50}
                                    interval={0}
                                    fontSize={9}
                                    stroke="#6c757d"
                                  />
                                  <YAxis 
                                    fontSize={9}
                                    stroke="#6c757d"
                                    width={30}
                                    domain={[0, 'dataMax + 10']}
                                  />
                                  <Tooltip 
                                    content={<CustomTooltip />}
                                    cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                                  />
                                  <Bar 
                                    dataKey="totalScore" 
                                    onClick={handleBarClick}
                                    radius={[2, 2, 0, 0]}
                                    minPointSize={8}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <LabelList content={<CustomLabel />} />
                                    {chartData.map((entry, index) => (
                                      <Cell 
                                        key={entry.uniqueKey}
                                        fill={entry.countsTowardsHandicap ? '#198754' : '#6c757d'} 
                                      />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                            <div className="text-center mt-2">
                              <small className="text-muted">
                                <i className="fas fa-arrow-left me-1"></i>
                                Swipe to see more games
                                <i className="fas fa-arrow-right ms-1"></i>
                              </small>
                            </div>
                          </div>
                        </div>

                        {/* Tablet: Medium responsive chart */}
                        <div className="d-none d-md-block d-lg-none" style={{ width: '100%', height: '370px' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={chartData}
                              margin={{
                                top: 35,
                                right: 20,
                                left: 15,
                                bottom: 70,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis 
                                dataKey="displayLabel"
                                tickFormatter={formatXAxisLabel}
                                angle={-45}
                                textAnchor="end"
                                height={70}
                                interval={0}
                                fontSize={10}
                                stroke="#666"
                              />
                              <YAxis 
                                fontSize={10}
                                stroke="#666"
                                width={35}
                                domain={[0, 'dataMax + 10']}
                              />
                              <Tooltip 
                                content={<CustomTooltip />}
                                cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                              />
                              <Bar 
                                dataKey="totalScore" 
                                onClick={handleBarClick}
                                radius={[2, 2, 0, 0]}
                                minPointSize={8}
                                style={{ cursor: 'pointer' }}
                              >
                                <LabelList content={<CustomLabel />} />
                                {chartData.map((entry, index) => (
                                  <Cell 
                                    key={entry.uniqueKey}
                                    fill={entry.countsTowardsHandicap ? '#198754' : '#6c757d'} 
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Desktop: Full responsive chart */}
                        <div className="d-none d-lg-block" style={{ width: '100%', height: '420px' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={chartData}
                              margin={{
                                top: 40,
                                right: 30,
                                left: 20,
                                bottom: 80,
                              }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis 
                                dataKey="displayLabel"
                                tickFormatter={formatXAxisLabel}
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                interval={0}
                                fontSize={12}
                                stroke="#666"
                              />
                              <YAxis 
                                fontSize={12}
                                stroke="#666"
                                label={{ value: 'Total Score', angle: -90, position: 'insideLeft' }}
                                domain={[0, 'dataMax + 15']}
                              />
                              <Tooltip 
                                content={<CustomTooltip />}
                                cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                              />
                              <Bar 
                                dataKey="totalScore" 
                                onClick={handleBarClick}
                                radius={[2, 2, 0, 0]}
                                minPointSize={8}
                                style={{ cursor: 'pointer' }}
                              >
                                <LabelList content={<CustomLabel />} />
                                {chartData.map((entry, index) => (
                                  <Cell 
                                    key={entry.uniqueKey}
                                    fill={entry.countsTowardsHandicap ? '#198754' : '#6c757d'} 
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <img src={golfIconDark} alt="No games" className="mb-3" style={{opacity: 0.5}} />
                        <p className="text-muted">No games recorded yet. Start playing to see your scores!</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-xl-4">
                <div className="handicapeCard p-3 rounded-3 bg-white h-100 d-flex flex-column flex-nowrap">
                  <div className="handicapeCardBody flex-fill h-100">
                    <div className="handicapDetail pt-0">
                      <div className="title lh-sm">Handicap details</div>
                      <div className="handicapeContent mb-0">
                        <div className="attestedScores rounded-1 text-center">
                          <div className="value text-white">{(handicap?.totalAverage ?? 0).toFixed(2)}</div>
                          <div className="title text-white">Current Handicap</div>
                        </div>
                        <div className="row otherDetail gap-12">
                          <div className="col-12">
                            <div className="value text-blue">{calculatedStats.lowHandicap}</div>
                            <div className="title">Low Handicap</div>
                          </div>
                          <div className="col-12">
                            <div className="value text-blue">{calculatedStats.differentialAvg}</div>
                            <div className="title">Differential Avg.</div>
                          </div>
                          <div className="col-12">
                            <div className="value text-blue">{calculatedStats.antiHandicap}</div>
                            <div className="title">Anti Handicap</div>
                          </div>
                          <div className="col-12 mt-3">
                            <div className="d-flex justify-content-between">
                              <small className="text-muted">Total Games:</small>
                              <small className="fw-medium">{calculatedStats.totalGames}</small>
                            </div>
                            <div className="d-flex justify-content-between">
                              <small className="text-muted">Best Scores Count:</small>
                              <small className="fw-medium text-success">
                                {calculatedStats.bestScoresCount}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="handicapRange p-3 bg-white rounded-3">
              <div className="handicapRangeBody d-flex flex-column gap-12">
                <div className="d-flex align-items-center gap-2 pb-3 border-bottom mb-1">
                  <div className="imgOuter">
                    <img src={handicapRangeIcon} alt="Handicap Range Icon" />
                  </div>
                  <div className="">
                    <div className="fs-6 fw-medium text-dark">How to Read The Data</div>
                    <div className="dec text-muted fs-14">
                      Click on any bar in the chart above to view detailed game information. Green bars represent your best 8 scores.
                    </div>
                  </div>
                </div>
                <div className="handicapRangeItem">
                  <div className="d-flex align-items-center gap-2 fs-6 fw-medium text-dark lh-sm">
                    <img src={golfIconDark} alt="Golf Icon" />
                    Game Scores
                  </div>
                  <div className="dec fs-14 text-muted mt-1 lh-sm">
                    Each bar represents your total score for a round of golf. Lower scores indicate better performance.
                  </div>
                </div>
                <div className="handicapRangeItem">
                  <div className="d-flex align-items-center gap-2 fs-6 fw-medium text-dark lh-sm">
                    <img src={golfIconDark} alt="Golf Icon" />
                    Best 8 Scores (Green Bars)
                  </div>
                  <div className="dec fs-14 text-muted mt-1 lh-sm">
                    Your handicap is calculated using your best 8 scores from your last 20 rounds. These are highlighted in green in the chart above.
                  </div>
                </div>
                <div className="handicapRangeItem">
                  <div className="d-flex align-items-center gap-2 fs-6 fw-medium text-dark lh-sm">
                    <img src={golfIconDark} alt="Golf Icon" />
                    Score Numbers
                  </div>
                  <div className="dec fs-14 text-muted mt-1 lh-sm">
                    The number displayed on top of each bar shows your exact score for that game.
                  </div>
                </div>
                <div className="handicapRangeItem">
                  <div className="d-flex align-items-center gap-2 fs-6 fw-medium text-dark lh-sm">
                    <img src={golfIconDark} alt="Golf Icon" />
                    Score Differential
                  </div>
                  <div className="dec fs-14 text-muted mt-1 lh-sm">
                    The difference between your score and the course rating, adjusted for slope. This standardizes scores across different courses and is used for handicap calculation.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Main>
  );
}

export default Handicap;






























// import React, { useState, useContext, useEffect, useRef } from "react";
// import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
// import Main from "../components/layout/Main";
// import graphImg from "../assets/images/graphImg.png";
// import golfIconDark from "../assets/images/golfIconDark.png";
// import handicapRangeIcon from "../assets/images/handicapRangeIcon.png";
// import { useNavigate, useLocation } from "react-router-dom";
// import useRequest from "../../src/hooks/useRequest";

// function Handicap() {
//   const { request } = useRequest();
//   const navigate = useNavigate();
//   const [average, setAverage] = useState();
//   const [handicap, setHandicap] = useState();
//   const [gameScores, setGameScores] = useState([]);
//   const [calculatedStats, setCalculatedStats] = useState({
//     lowHandicap: 0,
//     differentialAvg: 0,
//     antiHandicap: 0,
//     totalGames: 0,
//     bestScoresCount: 0
//   });
//   const [loading, setLoading] = useState(true);
//   const [show, scoreCardStepModal] = useState(false);
  
//   const scoreCardStepModalClose = () => scoreCardStepModal(false);
//   const scoreCardStepModalOpen = () => scoreCardStepModal(true);

//   useEffect(() => {
//     console.log("gameScores state updated:", gameScores);
//     console.log("gameScores length:", gameScores ? gameScores.length : 'null/undefined');
//   }, [gameScores]);

//   useEffect(() => {
//     fetchAllData();
//   }, []);

//   const fetchAllData = async () => {
//     setLoading(true);
//     try {
//       await fetchAverageData();
//       await fetchHandicapData();
//       await fetchGameScoresData();
//     } catch (error) {
//       console.error("Error fetching data:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchAverageData = () => {
//     return new Promise((resolve) => {
//       request({
//         url: `app/game/get_lowest_highest_Score`,
//         method: "GET",
//         onSuccess: (data) => {
//           console.log(data);
//           setAverage(data.data);
//           resolve();
//         },
//         onError: (error) => {
//           console.log(error);
//           resolve();
//         },
//       });
//     });
//   };

//   const fetchHandicapData = () => {
//     return new Promise((resolve) => {
//       request({
//         url: `/app/game/getHandicapScore`,
//         method: "GET",
//         onSuccess: (data) => {
//           setHandicap(data.data);
//           resolve();
//         },
//         onError: (error) => {
//           console.log(error);
//           resolve();
//         },
//       });
//     });
//   };

//   const fetchGameScoresData = () => {
//     return new Promise((resolve, reject) => {
//       request({
//         url: `/app/game/getHandicapGameScores`,
//         method: "GET",
//         onSuccess: (data) => {
//           console.log("Game scores data:", data);
          
//           // Handle your exact nested data structure: data.data.data
//           let scoresArray = [];
//           if (data.data && Array.isArray(data.data.data)) {
//             scoresArray = data.data.data;
//           } else if (Array.isArray(data.data)) {
//             scoresArray = data.data;
//           }
          
//           console.log("Final scoresArray:", scoresArray);
          
//           setGameScores(scoresArray);
//           calculateStatsFromGameScores(scoresArray);
          
//           resolve(scoresArray);
//         },
//         onError: (error) => {
//           console.error("Error fetching game scores:", error);
//           setGameScores([]);
//           reject(error);
//         },
//       });
//     });
//   };

//   const calculateStatsFromGameScores = (games) => {
//     if (!games || games.length === 0) {
//       setCalculatedStats({
//         lowHandicap: 0,
//         differentialAvg: 0,
//         antiHandicap: 0,
//         totalGames: 0,
//         bestScoresCount: 0
//       });
//       return;
//     }

//     const validDifferentials = games
//       .filter(game => game.handicapDifferential !== null && !isNaN(game.handicapDifferential))
//       .map(game => game.handicapDifferential);

//     const bestScores = games
//       .filter(game => game.countsTowardsHandicap && game.handicapDifferential !== null)
//       .map(game => game.handicapDifferential);

//     const totalGames = games.length;
//     const bestScoresCount = bestScores.length;
    
//     const lowHandicap = bestScores.length > 0 
//       ? bestScores.reduce((sum, diff) => sum + diff, 0) / bestScores.length 
//       : 0;

//     const differentialAvg = validDifferentials.length > 0 
//       ? validDifferentials.reduce((sum, diff) => sum + diff, 0) / validDifferentials.length 
//       : 0;

//     const antiHandicap = validDifferentials.length > 0 
//       ? Math.max(...validDifferentials) 
//       : 0;

//     setCalculatedStats({
//       lowHandicap: parseFloat(lowHandicap.toFixed(2)),
//       differentialAvg: parseFloat(differentialAvg.toFixed(2)),
//       antiHandicap: parseFloat(antiHandicap.toFixed(2)),
//       totalGames,
//       bestScoresCount
//     });
//   };

//   const handleScoreClick = (gameId) => {
//     console.log("Navigating to game:", gameId);
//     navigate(`/allScoreData/${gameId}`);
//   };

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-US', {
//       month: 'short',
//       day: '2-digit',
//       year: 'numeric'
//     });
//   };

//   const formatDateForChart = (dateString) => {
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-US', {
//       month: 'short',
//       day: '2-digit'
//     });
//   };

//   // FIXED: Create chart data with unique display keys for X-axis
//   const chartData = gameScores?.map((game, index) => {
//     const differential = game.handicapDifferential;
//     const formattedDifferential = differential !== null && !isNaN(differential) ? Number(differential) : 0;
    
//     return {
//       // CRITICAL: Use unique identifier for each data point
//       uniqueKey: `${game._id}_${index}`,
//       index: index,
//       id: game._id,
//       gameNumber: index + 1,
//       // FIXED: Use gameNumber for X-axis instead of date to ensure uniqueness
//       displayLabel: `Game ${index + 1}`,
//       date: formatDateForChart(game.createdAt),
//       differential: formattedDifferential,
//       countsTowardsHandicap: game.countsTowardsHandicap || false,
//       totalScore: game.totalScore || 0,
//       courseName: game.courseTee?.course?.name || 'Unknown Course',
//       teeName: game.courseTee?.name || 'Unknown Tee',
//       holes: game.holes || '18',
//       fullDate: formatDate(game.createdAt),
//       rawDifferential: differential,
//       timestamp: game.createdAt,
//       // Add all original game data for debugging
//       originalGame: game
//     };
//   }) || [];

//   console.log("Chart data created:", chartData.length, "points");

//   // FIXED TOOLTIP - Using index to get the correct data
//   const CustomTooltip = ({ active, payload, label }) => {
//     console.log("=== TOOLTIP DEBUG START ===");
//     console.log("Tooltip active:", active);
//     console.log("Tooltip label:", label);
//     console.log("Tooltip payload:", payload);

//     if (!active || !payload || payload.length === 0) {
//       console.log("Tooltip: Not active or no payload");
//       return null;
//     }

//     // Get the actual data point from the payload
//     const dataPoint = payload[0]?.payload;
//     console.log("Tooltip dataPoint:", dataPoint);

//     if (!dataPoint) {
//       console.log("Tooltip: No data point found");
//       return null;
//     }

//     // Log the specific game data being shown
//     console.log("Showing tooltip for game:", {
//       id: dataPoint.id,
//       gameNumber: dataPoint.gameNumber,
//       totalScore: dataPoint.totalScore,
//       courseName: dataPoint.courseName,
//       teeName: dataPoint.teeName,
//       differential: dataPoint.differential,
//       countsTowardsHandicap: dataPoint.countsTowardsHandicap
//     });

//     console.log("=== TOOLTIP DEBUG END ===");

//     return (
//       <div className="bg-white p-3 border rounded shadow-lg" style={{ minWidth: '250px', zIndex: 1000 }}>
//         <div className="mb-2">
//           <small className="text-primary fw-bold">Game #{dataPoint.gameNumber}</small>
//         </div>
//         <div className="mb-2">
//           <div className="fw-medium">{dataPoint.courseName}</div>
//           <div className="text-muted small">{dataPoint.teeName} • {dataPoint.holes} holes</div>
//         </div>
//         <div className="mb-2">
//           <div>Score: <strong className="text-dark">{dataPoint.totalScore}</strong></div>
//           <div>Differential: <strong className="text-dark">
//             {dataPoint.differential !== null ? dataPoint.differential.toFixed(1) : 'N/A'}
//           </strong></div>
//         </div>
//         <div className="mb-2">
//           <small className="text-muted">{dataPoint.fullDate}</small>
//         </div>
//         {dataPoint.countsTowardsHandicap && (
//           <div className="mb-2">
//             <small className="badge bg-success">✓ Counts towards handicap</small>
//           </div>
//         )}
//         <div className="border-top pt-2">
//           <small className="text-muted">ID: {dataPoint.id.slice(-8)}</small>
//         </div>
//       </div>
//     );
//   };

//   // FIXED BAR CLICK HANDLER
//   const handleBarClick = (data, index, event) => {
//     console.log("=== BAR CLICK DEBUG ===");
//     console.log("Click data:", data);
//     console.log("Click index:", index);
//     console.log("Click event:", event);
    
//     if (data && data.id) {
//       console.log("Navigating to game ID:", data.id);
//       handleScoreClick(data.id);
//     } else {
//       console.log("No valid game ID found in click data");
//     }
//   };

//   // FIXED: Custom label formatter for X-axis to show dates but use unique keys
//   const formatXAxisLabel = (value, index) => {
//     const dataPoint = chartData[index];
//     return dataPoint ? dataPoint.date : value;
//   };

//   if (loading) {
//     return (
//       <Main>
//         <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
//           <div className="spinner-border text-primary" role="status">
//             <span className="visually-hidden">Loading...</span>
//           </div>
//         </div>
//       </Main>
//     );
//   }

//   return (
//     <Main>
//       <section className="home-wrap px-lg-4 px-3 pb-lg-4 pb-3 container-fluid">
//         <div className="row g-3">
//           <div className="col-12">
//             <div className="row g-3">
//               <div className="col-xl-8">
//                 <div className="handicapeCard p-3 rounded-3 bg-white h-100 d-flex flex-column flex-nowrap">
//                   <div className="handicapeCardHeader">
//                     <div className="title mb-1">Score Differentials</div>
//                     <div className="graphIndicater d-flex flex-column flex-sm-row align-items-sm-center gap-sm-4 gap-2 mb-3">
//                       <div className="graphIndicaterItem handicap d-flex gap-1">
//                         <span className="badge bg-success me-1"></span>
//                         Count Towards Handicap
//                       </div>
//                       <div className="graphIndicaterItem d-flex gap-1">
//                         <span className="badge bg-secondary me-1"></span>
//                         Not for in Hdcp yet
//                       </div>
//                     </div>
//                   </div>
                  
//                   <div className="handicapeCardBody flex-fill h-100">
//                     {/* Debug info */}
//                     <div className="mb-3">
//                       <small className="text-muted">
//                         Games: {gameScores ? gameScores.length : 'null'}, 
//                         Chart points: {chartData.length},
//                         Best count: {calculatedStats.bestScoresCount}
//                       </small>
//                     </div>

//                     {gameScores && gameScores.length > 0 ? (
//                       <div className="chart-container">
//                         {/* Mobile: Horizontal scroll wrapper */}
//                         <div className="d-block d-md-none">
//                           <div className="position-relative">
//                             <div 
//                               style={{ 
//                                 width: `${Math.max(400, chartData.length * 40)}px`, 
//                                 height: '280px',
//                                 overflowX: 'auto',
//                                 overflowY: 'hidden'
//                               }}
//                               className="border rounded bg-light"
//                             >
//                               <ResponsiveContainer width="100%" height="100%">
//                                 <BarChart
//                                   data={chartData}
//                                   margin={{
//                                     top: 10,
//                                     right: 10,
//                                     left: 10,
//                                     bottom: 50,
//                                   }}
//                                 >
//                                   <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
//                                   <XAxis 
//                                     dataKey="displayLabel"
//                                     tickFormatter={formatXAxisLabel}
//                                     angle={-45}
//                                     textAnchor="end"
//                                     height={50}
//                                     interval={0}
//                                     fontSize={9}
//                                     stroke="#6c757d"
//                                   />
//                                   <YAxis 
//                                     fontSize={9}
//                                     stroke="#6c757d"
//                                     width={30}
//                                   />
//                                   <Tooltip 
//                                     content={<CustomTooltip />}
//                                     cursor={{ fill: 'rgba(0,0,0,0.1)' }}
//                                   />
//                                   <Bar 
//                                     dataKey="differential" 
//                                     onClick={handleBarClick}
//                                     radius={[2, 2, 0, 0]}
//                                     minPointSize={8}
//                                     style={{ cursor: 'pointer' }}
//                                   >
//                                     {chartData.map((entry, index) => (
//                                       <Cell 
//                                         key={entry.uniqueKey}
//                                         fill={entry.countsTowardsHandicap ? '#012b28' : '#6c757d'} 
//                                       />
//                                     ))}
//                                   </Bar>
//                                 </BarChart>
//                               </ResponsiveContainer>
//                             </div>
//                             <div className="text-center mt-2">
//                               <small className="text-muted">
//                                 <i className="fas fa-arrow-left me-1"></i>
//                                 Swipe to see more games
//                                 <i className="fas fa-arrow-right ms-1"></i>
//                               </small>
//                             </div>
//                           </div>
//                         </div>

//                         {/* Tablet: Medium responsive chart */}
//                         <div className="d-none d-md-block d-lg-none" style={{ width: '100%', height: '350px' }}>
//                           <ResponsiveContainer width="100%" height="100%">
//                             <BarChart
//                               data={chartData}
//                               margin={{
//                                 top: 15,
//                                 right: 20,
//                                 left: 15,
//                                 bottom: 70,
//                               }}
//                             >
//                               <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
//                               <XAxis 
//                                 dataKey="displayLabel"
//                                 tickFormatter={formatXAxisLabel}
//                                 angle={-45}
//                                 textAnchor="end"
//                                 height={70}
//                                 interval={0}
//                                 fontSize={10}
//                                 stroke="#666"
//                               />
//                               <YAxis 
//                                 fontSize={10}
//                                 stroke="#666"
//                                 width={35}
//                               />
//                               <Tooltip 
//                                 content={<CustomTooltip />}
//                                 cursor={{ fill: 'rgba(0,0,0,0.1)' }}
//                               />
//                               <Bar 
//                                 dataKey="differential" 
//                                 onClick={handleBarClick}
//                                 radius={[2, 2, 0, 0]}
//                                 minPointSize={8}
//                                 style={{ cursor: 'pointer' }}
//                               >
//                                 {chartData.map((entry, index) => (
//                                   <Cell 
//                                     key={entry.uniqueKey}
//                                     fill={entry.countsTowardsHandicap ? '#012b28' : '#6c757d'} 
//                                   />
//                                 ))}
//                               </Bar>
//                             </BarChart>
//                           </ResponsiveContainer>
//                         </div>

//                         {/* Desktop: Full responsive chart */}
//                         <div className="d-none d-lg-block" style={{ width: '100%', height: '400px' }}>
//                           <ResponsiveContainer width="100%" height="100%">
//                             <BarChart
//                               data={chartData}
//                               margin={{
//                                 top: 20,
//                                 right: 30,
//                                 left: 20,
//                                 bottom: 80,
//                               }}
//                             >
//                               <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
//                               <XAxis 
//                                 dataKey="displayLabel"
//                                 tickFormatter={formatXAxisLabel}
//                                 angle={-45}
//                                 textAnchor="end"
//                                 height={80}
//                                 interval={0}
//                                 fontSize={12}
//                                 stroke="#666"
//                               />
//                               <YAxis 
//                                 fontSize={12}
//                                 stroke="#666"
//                                 label={{ value: 'Score Differential', angle: -90, position: 'insideLeft' }}
//                               />
//                               <Tooltip 
//                                 content={<CustomTooltip />}
//                                 cursor={{ fill: '#012b28' }}
//                               />
//                               <Bar 
//                                 dataKey="differential" 
//                                 onClick={handleBarClick}
//                                 radius={[2, 2, 0, 0]}
//                                 minPointSize={8}
//                                 style={{ cursor: 'pointer' }}
//                               >
//                                 {chartData.map((entry, index) => (
//                                   <Cell 
//                                     key={entry.uniqueKey}
//                                     fill={entry.countsTowardsHandicap ? '#012b28' : '#6c757d'} 
//                                   />
//                                 ))}
//                               </Bar>
//                             </BarChart>
//                           </ResponsiveContainer>
//                         </div>
//                       </div>
//                     ) : (
//                       <div className="text-center py-5">
//                         <img src={golfIconDark} alt="No games" className="mb-3" style={{opacity: 0.5}} />
//                         <p className="text-muted">No games recorded yet. Start playing to see your handicap!</p>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//               <div className="col-xl-4">
//                 <div className="handicapeCard p-3 rounded-3 bg-white h-100 d-flex flex-column flex-nowrap">
//                   <div className="handicapeCardBody flex-fill h-100">
//                     <div className="handicapDetail pt-0">
//                       <div className="title lh-sm">Handicap details</div>
//                       <div className="handicapeContent mb-0">
//                         <div className="attestedScores rounded-1 text-center">
//                           <div className="value text-white">{(handicap?.totalAverage ?? 0).toFixed(2)}</div>
//                           <div className="title text-white">Current Handicap</div>
//                         </div>
//                         <div className="row otherDetail gap-12">
//                           <div className="col-12">
//                             <div className="value text-blue">{calculatedStats.lowHandicap}</div>
//                             <div className="title">Low Handicap</div>
//                           </div>
//                           <div className="col-12">
//                             <div className="value text-blue">{calculatedStats.differentialAvg}</div>
//                             <div className="title">Differential Avg.</div>
//                           </div>
//                           <div className="col-12">
//                             <div className="value text-blue">{calculatedStats.antiHandicap}</div>
//                             <div className="title">Anti Handicap</div>
//                           </div>
//                           <div className="col-12 mt-3">
//                             <div className="d-flex justify-content-between">
//                               <small className="text-muted">Total Games:</small>
//                               <small className="fw-medium">{calculatedStats.totalGames}</small>
//                             </div>
//                             <div className="d-flex justify-content-between">
//                               <small className="text-muted">Best Scores Count:</small>
//                               <small className="fw-medium text-success">
//                                 {calculatedStats.bestScoresCount}
//                               </small>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="col-12">
//             <div className="handicapRange p-3 bg-white rounded-3">
//               <div className="handicapRangeBody d-flex flex-column gap-12">
//                 <div className="d-flex align-items-center gap-2 pb-3 border-bottom mb-1">
//                   <div className="imgOuter">
//                     <img src={handicapRangeIcon} alt="Handicap Range Icon" />
//                   </div>
//                   <div className="">
//                     <div className="fs-6 fw-medium text-dark">How to Read The Data</div>
//                     <div className="dec text-muted fs-14">
//                       Click on any bar in the chart above to view detailed game information
//                     </div>
//                   </div>
//                 </div>
//                 <div className="handicapRangeItem">
//                   <div className="d-flex align-items-center gap-2 fs-6 fw-medium text-dark lh-sm">
//                     <img src={golfIconDark} alt="Golf Icon" />
//                     Handicap
//                   </div>
//                   <div className="dec fs-14 text-muted mt-1 lh-sm">
//                     Handicap refers to a golfer's playing ability. It is measured by the number of strokes above or below par that a player is expected to score on a course.
//                   </div>
//                 </div>
//                 <div className="handicapRangeItem">
//                   <div className="d-flex align-items-center gap-2 fs-6 fw-medium text-dark lh-sm">
//                     <img src={golfIconDark} alt="Golf Icon" />
//                     Best 8 Scores
//                   </div>
//                   <div className="dec fs-14 text-muted mt-1 lh-sm">
//                     Your handicap is calculated using your best 8 scores from your last 20 rounds. These are highlighted in green in the chart above.
//                   </div>
//                 </div>
//                 <div className="handicapRangeItem">
//                   <div className="d-flex align-items-center gap-2 fs-6 fw-medium text-dark lh-sm">
//                     <img src={golfIconDark} alt="Golf Icon" />
//                     Score Differential
//                   </div>
//                   <div className="dec fs-14 text-muted mt-1 lh-sm">
//                     The difference between your score and the course rating, adjusted for slope. This standardizes scores across different courses.
//                   </div>
//                 </div>
//                 <div className="handicapRangeItem">
//                   <div className="d-flex align-items-center gap-2 fs-6 fw-medium text-dark lh-sm">
//                     <img src={golfIconDark} alt="Golf Icon" />
//                     Anti Handicap
//                   </div>
//                   <div className="dec fs-14 text-muted mt-1 lh-sm">
//                     Anti Handicap is a theoretical concept representing the opposite of a player's handicap. It is used for comparison purposes and indicates areas where improvement is needed.
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>
//     </Main>
//   );
// }

// export default Handicap;
